import { useState, useEffect } from "react";
import { Copy, Check, Star, DollarSign, Users, Gift, ChevronRight } from "lucide-react";
import Dh from "../components/Dh";
import AppShell, { ACCENT, ACCENT_DEEP } from "../components/AppShell";
import { useAuth } from "../hooks/useAuth";
import { API_BASE_URL } from "@/utils/env";
import { isCorporate } from "@/brand";

const BASE_URL = "https://conncct.com";

/* The hero keeps its dark plate on both brands, but the wash follows the brand:
   navy with a gold glow on connectcorporate.com, navy with blue on conncct.com.
   The gold sits on the dark panel only — it is the one place the brand yellow
   can carry text, and `text-amber-400` on this ground is what it is for. */
const HERO_BG = isCorporate
    ? "linear-gradient(135deg, #0C1B30 0%, #17263A 58%, #0A1728 100%)"
    : "linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0f2240 100%)";
const HERO_GLOW = isCorporate ? "#FFD54F" : "#3B82F6";

type Referral = { uid: string; name: string; email: string; signupDate: string | null; status: string };

const MILESTONES = [
    { count: 1, reward: 150, label: "First Referral Bonus" },
    { count: 3, reward: 500, label: "Triple Bonus" },
    { count: 5, reward: 1000, label: "Power Referrer" },
    { count: 10, reward: 2500, label: "Elite Referrer" },
];

const SOCIAL = [
    {
        id: "facebook",
        label: "Facebook",
        bg: "#1877F2",
        hoverBg: "#166FE5",
        icon: (
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
        ),
        getUrl: (link: string) =>
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
    },
    {
        id: "twitter",
        label: "X",
        bg: "#000000",
        hoverBg: "#111111",
        icon: (
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
        ),
        getUrl: (link: string) =>
            `https://twitter.com/intent/tweet?url=${encodeURIComponent(link)}&text=${encodeURIComponent("Join me on Conncct, the smarter way to grow your business. Get started with my referral link:")}`,
    },
    {
        id: "whatsapp",
        label: "WhatsApp",
        bg: "#25D366",
        hoverBg: "#20BD5A",
        icon: (
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
        ),
        getUrl: (link: string) =>
            `https://wa.me/?text=${encodeURIComponent(`Join me on Conncct, the smarter way to grow your business.\n${link}`)}`,
    },
    {
        id: "messenger",
        label: "Messenger",
        bg: "#0084FF",
        hoverBg: "#0078EB",
        icon: (
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.652V24l4.088-2.242c1.092.301 2.246.464 3.443.464 6.627 0 12-4.974 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.26L19.752 8l-6.561 6.963z" />
            </svg>
        ),
        getUrl: (link: string) =>
            `https://www.facebook.com/dialog/send?link=${encodeURIComponent(link)}&app_id=291494419107518`,
    },
    {
        id: "email",
        label: "Email",
        bg: "#EA4335",
        hoverBg: "#D33426",
        icon: (
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
            </svg>
        ),
        getUrl: (link: string) =>
            `mailto:?subject=${encodeURIComponent("Join me on Conncct")}&body=${encodeURIComponent(`Hi,\n\nI've been using Conncct to grow my business and thought you'd find it valuable too.\n\nSign up using my referral link and we both benefit:\n${link}\n\nBest,`)}`,
    },
];

export default function ReferAndEarn() {
    const { user: authUser, logout } = useAuth();
    const [copied, setCopied] = useState(false);
    const [referralCode, setReferralCode] = useState("REF00000000");
    const [referralLink, setReferralLink] = useState(`${BASE_URL}/signup?ref=REF00000000`);
    const [referrals, setReferrals] = useState<Referral[]>([]);

    useEffect(() => {
        if (!authUser?.uid) return;
        setReferralCode("REF" + authUser.uid.slice(0, 10).toUpperCase());
        const origin = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : BASE_URL;
        // The link carries the referrer's uid so signup can attribute it directly.
        setReferralLink(`${origin}/signup?ref=${authUser.uid}`);
        (async () => {
            try {
                const tokensStr = localStorage.getItem('authTokens');
                const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
                const resp = await fetch(`${API_BASE_URL}/auth/referrals/${authUser.uid}`, {
                    headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
                });
                if (resp.ok) { const d = await resp.json(); if (Array.isArray(d.referrals)) setReferrals(d.referrals); }
            } catch { /* keep empty on error */ }
        })();
    }, [authUser]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(referralLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            const el = document.createElement("textarea");
            el.value = referralLink;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        }
    };

    const handleShare = (svc: typeof SOCIAL[0]) => {
        window.open(svc.getUrl(referralLink), "_blank", "noopener,noreferrer,width=600,height=500");
    };

    const referralCount = referrals.length;
    // Rewards unlock on a CONFIRMED referral (signed up AND made a first purchase).
    // The amount is tracked here; payouts are settled by the team.
    const confirmedCount = referrals.filter(r => r.status === 'confirmed').length;
    const totalEarned = MILESTONES.filter(m => confirmedCount >= m.count).reduce((s, m) => s + m.reward, 0);
    const nextMilestone = MILESTONES.find(m => m.count > confirmedCount) || MILESTONES[MILESTONES.length - 1];
    const fmtDate = (s: string | null) => s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

    return (
        <AppShell
            title="Refer & Earn"
            subtitle="Share your link, earn 30% back on a referral's first purchase."
            crumb="Refer & Earn"
            onLogout={logout}
        >
            <div>

                    {/* Hero banner */}
                    <div
                        className="relative rounded-3xl overflow-hidden mb-8 px-8 py-10 text-center"
                        style={{ background: HERO_BG }}
                    >
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-40 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ backgroundColor: HERO_GLOW }} />
                        <div className="relative z-10">
                            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-5" style={{ background: "rgba(250,204,21,0.15)", border: "1px solid rgba(250,204,21,0.3)" }}>
                                <Gift className="w-3.5 h-3.5 text-amber-400" />
                                <span className="text-xs font-bold text-amber-400 tracking-wide">REFERRAL PROGRAM</span>
                            </div>
                            {/* h2, not h1 — the shell's page title owns the h1 now. */}
                            <h2 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight">Earn Rewards</h2>
                            <p className="text-slate-300 text-sm md:text-base leading-relaxed max-w-lg mx-auto">
                                Share your referral link, earn <span className="text-amber-400 font-bold">30% back</span> when someone signs up and makes their first purchase.{" "}
                                <a href="https://conncct.com/terms" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white underline transition">
                                    Terms apply
                                </a>
                            </p>
                        </div>
                    </div>

                    {/* Referral link card */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-5">
                        <p className="text-xs font-semibold text-slate-500 mb-3">Your unique referral link</p>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 flex items-center rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 gap-2 min-w-0">
                                <span className="text-sm text-slate-700 font-mono truncate">{referralLink}</span>
                            </div>
                            <button
                                onClick={handleCopy}
                                className="shrink-0 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white transition-all shadow-sm active:scale-95"
                                style={{ backgroundColor: copied ? "#10B981" : ACCENT }}
                                onMouseEnter={e => { if (!copied) e.currentTarget.style.backgroundColor = ACCENT_DEEP; }}
                                onMouseLeave={e => { if (!copied) e.currentTarget.style.backgroundColor = ACCENT; }}
                            >
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                <span className="hidden sm:inline">{copied ? "Copied!" : "Copy"}</span>
                            </button>
                        </div>

                        {/* Social share */}
                        <div className="mt-5 pt-5 border-t border-slate-100">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Share via</p>
                            <div className="flex items-center gap-3 flex-wrap">
                                {SOCIAL.map(s => (
                                    <button
                                        key={s.id}
                                        onClick={() => handleShare(s)}
                                        title={s.label}
                                        className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95 shadow-sm"
                                        style={{ backgroundColor: s.bg }}
                                    >
                                        {s.icon}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Stats row */}
                    <div className="grid grid-cols-2 gap-4 mb-5">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col items-center text-center">
                            <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center mb-3">
                                <Star className="w-5 h-5 text-amber-500" />
                            </div>
                            <p className="text-2xl font-black text-gray-900">{referralCount}</p>
                            <p className="text-sm font-semibold text-gray-700 mt-0.5">Your Referrals</p>
                            <p className="text-[11px] text-gray-400 mt-1">{confirmedCount} confirmed</p>
                        </div>
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col items-center text-center">
                            <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center mb-3">
                                <DollarSign className="w-5 h-5 text-green-500" />
                            </div>
                            <p className="text-2xl font-black text-gray-900"><Dh/>{totalEarned.toLocaleString()}</p>
                            <p className="text-sm font-semibold text-gray-700 mt-0.5">Rewards Unlocked</p>
                            <p className="text-[11px] text-gray-400 mt-1">Settled by our team</p>
                        </div>
                    </div>

                    {/* Milestone tracker */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-5">
                        <div className="flex items-center gap-2 mb-5">
                            <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                                <Gift className="w-4 h-4 text-amber-500" />
                            </div>
                            <h3 className="text-sm font-bold text-gray-900">Reward Milestones</h3>
                        </div>
                        <div className="space-y-3">
                            {MILESTONES.map((m, i) => {
                                const done = confirmedCount >= m.count;
                                const isCurrent = !done && (i === 0 || confirmedCount >= MILESTONES[i - 1].count);
                                return (
                                    <div
                                        key={m.count}
                                        className="flex items-center gap-4 p-3.5 rounded-xl transition"
                                        style={{
                                            backgroundColor: done ? "#F0FDF4" : isCurrent ? "#FFFBEB" : "#F9FAFB",
                                            border: isCurrent ? "1px solid #FCD34D" : "1px solid transparent",
                                        }}
                                    >
                                        <div
                                            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-black"
                                            style={{
                                                backgroundColor: done ? "#10B981" : isCurrent ? "#2159B8" : "#E5E7EB",
                                                color: done || isCurrent ? "#fff" : "#9CA3AF",
                                            }}
                                        >
                                            {done ? "✓" : m.count}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-semibold ${done ? "text-green-700" : isCurrent ? "text-amber-700" : "text-gray-400"}`}>
                                                {m.count} {m.count === 1 ? "Referral" : "Referrals"}: {m.label}
                                            </p>
                                        </div>
                                        <span
                                            className="text-sm font-black shrink-0"
                                            style={{ color: done ? "#10B981" : isCurrent ? "#2159B8" : "#D1D5DB" }}
                                        >
                                            <Dh em="0.8"/>{m.reward.toLocaleString()}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        {!confirmedCount && (
                            <p className="text-[11px] text-gray-400 text-center mt-4">
                                Rewards unlock when a referral makes their first purchase →
                            </p>
                        )}
                    </div>

                    {/* Activity table */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-400" />
                            <h3 className="text-sm font-bold text-gray-900">Referral Activity</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-50">
                                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Name · Email</th>
                                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Sign Up Date</th>
                                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Confirmed Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {referrals.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-12 text-center">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center">
                                                        <Users className="w-6 h-6 text-slate-300" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-500">No referrals yet</p>
                                                        <p className="text-xs text-gray-400 mt-0.5">Share your link to start earning rewards</p>
                                                    </div>
                                                    <button
                                                        onClick={handleCopy}
                                                        className="acc-text mt-1 flex items-center gap-1.5 text-xs font-bold transition"
                                                    >
                                                        <Copy className="w-3.5 h-3.5" />
                                                        Copy referral link
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : referrals.map(r => (
                                        <tr key={r.uid} className="border-t border-slate-100">
                                            <td className="px-6 py-3.5">
                                                <div className="font-semibold text-gray-800">{r.name}</div>
                                                {r.email && <div className="text-xs text-gray-400">{r.email}</div>}
                                            </td>
                                            <td className="px-6 py-3.5 text-gray-600">{fmtDate(r.signupDate)}</td>
                                            <td className="px-6 py-3.5">
                                                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ${r.status === 'confirmed' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                                                    {r.status === 'confirmed' ? 'Confirmed' : 'Signed up'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3.5 text-gray-600">{r.status === 'confirmed' ? fmtDate(r.signupDate) : 'Pending'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

            </div>
        </AppShell>
    );
}
